import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

export interface Theme {
  name: string;
  background: string;
  surface: string;
  surfaceHover: string;
  text: string;
  textSecondary: string;
  textDisabled: string;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryLight: string;
  secondary: string;
  sidebar: string;
  button: string;
  accent: string;
  border: string;
  borderLight: string;
  borderDark: string;
  contrastText: string;
  isDark: boolean;
}

const hexToRgb = (hex: string) => {
  const sanitized = hex.replace('#', '');
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((value) => value + value)
          .join('')
      : sanitized;

  const value = Number.parseInt(normalized, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const hexToRgba = (hex: string, alpha: number) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const themes: Record<string, Theme> = {
  light: {
    name: 'Light',
    background: '#f5f7fa',
    surface: '#ffffff',
    surfaceHover: '#f8fafc',
    text: '#1f2937',
    textSecondary: '#64748b',
    textDisabled: '#94a3b8',
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    primaryActive: '#1e40af',
    primaryLight: '#60a5fa',
    secondary: '#94a3b8',
    sidebar: '#ffffff',
    button: '#2563eb',
    accent: '#10b981',
    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    borderDark: '#cbd5e1',
    contrastText: '#ffffff',
    isDark: false,
  },
  dark: {
    name: 'Dark',
    background: '#020617',
    surface: '#111827',
    surfaceHover: '#1f2937',
    text: '#f8fafc',
    textSecondary: '#cbd5e1',
    textDisabled: '#94a3b8',
    primary: '#8b5cf6',
    primaryHover: '#7c3aed',
    primaryActive: '#6d28d9',
    primaryLight: '#c4b5fd',
    secondary: '#22d3ee',
    sidebar: '#0f172a',
    button: '#8b5cf6',
    accent: '#fb7185',
    border: '#334155',
    borderLight: '#1e293b',
    borderDark: '#475569',
    contrastText: '#ffffff',
    isDark: true,
  },
  blue: {
    name: 'Blue',
    background: '#eff6ff',
    surface: '#ffffff',
    surfaceHover: '#dbeafe',
    text: '#1e3a8a',
    textSecondary: '#2563eb',
    textDisabled: '#93c5fd',
    primary: '#1976d2',
    primaryHover: '#1565c0',
    primaryActive: '#0d47a1',
    primaryLight: '#42a5f5',
    secondary: '#42a5f5',
    sidebar: '#dbeafe',
    button: '#1976d2',
    accent: '#1565c0',
    border: '#bfdbfe',
    borderLight: '#dbeafe',
    borderDark: '#93c5fd',
    contrastText: '#ffffff',
    isDark: false,
  },
  green: {
    name: 'Green',
    background: '#f0fdf4',
    surface: '#ffffff',
    surfaceHover: '#dcfce7',
    text: '#14532d',
    textSecondary: '#15803d',
    textDisabled: '#86efac',
    primary: '#388e3c',
    primaryHover: '#2e7d32',
    primaryActive: '#1b5e20',
    primaryLight: '#66bb6a',
    secondary: '#66bb6a',
    sidebar: '#dcfce7',
    button: '#388e3c',
    accent: '#2e7d32',
    border: '#bbf7d0',
    borderLight: '#dcfce7',
    borderDark: '#86efac',
    contrastText: '#ffffff',
    isDark: false,
  },
  purple: {
    name: 'Purple',
    background: '#faf5ff',
    surface: '#ffffff',
    surfaceHover: '#f3e8ff',
    text: '#581c87',
    textSecondary: '#7c3aed',
    textDisabled: '#c4b5fd',
    primary: '#7b1fa2',
    primaryHover: '#6a1b9a',
    primaryActive: '#4a148c',
    primaryLight: '#ba68c8',
    secondary: '#ba68c8',
    sidebar: '#f3e8ff',
    button: '#7b1fa2',
    accent: '#6a1b9a',
    border: '#e9d5ff',
    borderLight: '#f3e8ff',
    borderDark: '#c4b5fd',
    contrastText: '#ffffff',
    isDark: false,
  },
  orange: {
    name: 'Orange',
    background: '#fff7ed',
    surface: '#ffffff',
    surfaceHover: '#ffedd5',
    text: '#9a3412',
    textSecondary: '#ea580c',
    textDisabled: '#fdba74',
    primary: '#f57c00',
    primaryHover: '#ef6c00',
    primaryActive: '#c2410c',
    primaryLight: '#ffb74d',
    secondary: '#ffb74d',
    sidebar: '#ffedd5',
    button: '#f57c00',
    accent: '#ef6c00',
    border: '#fed7aa',
    borderLight: '#ffedd5',
    borderDark: '#fdba74',
    contrastText: '#ffffff',
    isDark: false,
  },
  teal: {
    name: 'Teal',
    background: '#f0fdfa',
    surface: '#ffffff',
    surfaceHover: '#ccfbf1',
    text: '#134e4a',
    textSecondary: '#0f766e',
    textDisabled: '#5eead4',
    primary: '#00796b',
    primaryHover: '#00695c',
    primaryActive: '#004d40',
    primaryLight: '#4db6ac',
    secondary: '#4db6ac',
    sidebar: '#ccfbf1',
    button: '#00796b',
    accent: '#00695c',
    border: '#99f6e4',
    borderLight: '#ccfbf1',
    borderDark: '#5eead4',
    contrastText: '#ffffff',
    isDark: false,
  },
  'high-contrast': {
    name: 'High Contrast',
    background: '#ffffff',
    surface: '#ffffff',
    surfaceHover: '#f3f4f6',
    text: '#000000',
    textSecondary: '#111111',
    textDisabled: '#525252',
    primary: '#000000',
    primaryHover: '#1f2937',
    primaryActive: '#000000',
    primaryLight: '#404040',
    secondary: '#ffffff',
    sidebar: '#ffffff',
    button: '#000000',
    accent: '#ff0000',
    border: '#000000',
    borderLight: '#525252',
    borderDark: '#000000',
    contrastText: '#ffffff',
    isDark: false,
  },
};

const availableThemes = Object.keys(themes);

const applyThemeToDocument = (themeName: string, theme: Theme) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const themeVars: Record<string, string> = {
    '--brand-primary': theme.primary,
    '--brand-hover': theme.primaryHover,
    '--brand-active': theme.primaryActive,
    '--brand-gray': theme.secondary,
    '--color-primary': theme.primary,
    '--color-primary-light': theme.primaryLight,
    '--color-primary-dark': theme.primaryActive,
    '--color-primary-50': hexToRgba(theme.primary, 0.08),
    '--color-primary-100': hexToRgba(theme.primary, 0.14),
    '--color-primary-200': hexToRgba(theme.primary, 0.24),
    '--color-secondary': theme.secondary,
    '--color-secondary-light': hexToRgba(theme.secondary, 0.65),
    '--color-secondary-dark': theme.borderDark,
    '--color-background': theme.surface,
    '--color-background-secondary': theme.background,
    '--color-background-tertiary': theme.surfaceHover,
    '--color-surface': theme.surface,
    '--color-surface-hover': theme.surfaceHover,
    '--color-text-primary': theme.text,
    '--color-text-secondary': theme.textSecondary,
    '--color-text-disabled': theme.textDisabled,
    '--color-text-inverse': theme.contrastText,
    '--color-border': theme.border,
    '--color-border-light': theme.borderLight,
    '--color-border-dark': theme.borderDark,
    '--color-gray-50': theme.background,
    '--color-gray-100': theme.surfaceHover,
    '--color-gray-200': theme.borderLight,
    '--color-gray-300': theme.border,
    '--color-gray-400': theme.textDisabled,
    '--color-gray-500': theme.textSecondary,
    '--color-gray-600': theme.text,
    '--color-gray-700': theme.text,
    '--color-gray-800': theme.text,
    '--color-gray-900': theme.text,
    '--app-overlay': theme.isDark ? 'rgba(2, 6, 23, 0.78)' : 'rgba(15, 23, 42, 0.45)',
  };

  root.dataset.appTheme = themeName;
  root.style.colorScheme = theme.isDark ? 'dark' : 'light';

  Object.entries(themeVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  document.body.style.backgroundColor = theme.background;
  document.body.style.color = theme.text;
};

interface ThemeContextType {
  theme: Theme;
  themeName: string;
  setTheme: (themeName: string) => void;
  availableThemes: string[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const getStoredThemeName = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const savedTheme = window.localStorage.getItem('theme');
  return savedTheme && themes[savedTheme] ? savedTheme : 'light';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeName, setThemeName] = useState<string>(() => getStoredThemeName());
  const theme = useMemo(() => themes[themeName] ?? themes.light, [themeName]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('theme', themeName);
    }

    applyThemeToDocument(themeName, theme);
  }, [theme, themeName]);

  const setTheme = useCallback((newThemeName: string) => {
    if (themes[newThemeName]) {
      setThemeName((currentThemeName) => (
        currentThemeName === newThemeName ? currentThemeName : newThemeName
      ));
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      themeName,
      setTheme,
      availableThemes,
    }),
    [setTheme, theme, themeName]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
